// Loads the Snipcart cart and checkout from its CDN. This mirrors the loader in
// https://docs.snipcart.com/v3/setup/installation, but runs only when Snipcart is
// configured so environments without it never fetch the script.
//
// Pinned to an exact release rather than Snipcart's moving "3.0" alias, so a Snipcart
// release cannot change the JavaScript on the payment page without a code change here.
// Snipcart publishes no subresource integrity hashes, so the script is not hash-checked.
const SNIPCART_VERSION = '3.9.0';
const SNIPCART_CDN = 'https://cdn.snipcart.com';

// Shared with the hidden product definition in public/index.html, which Snipcart
// crawls to validate the price of anything added to its cart.
export const SNIPCART_LOLLIPOP_ID = 'lollipop';
export const SNIPCART_LOLLIPOP_NAME = 'Red Heart Lollipop, unwrapped';

const SNIPCART_PUBLIC_API_KEY = process.env.REACT_APP_SNIPCART_PUBLIC_API_KEY;

// The price Snipcart validates against is interpolated into public/index.html from
// REACT_APP_BLINKPAY_AMOUNT at build time. Without it the placeholder is left in place
// and every order would fail validation, so Snipcart is treated as not configured.
export const snipcartConfigured = Boolean(SNIPCART_PUBLIC_API_KEY && process.env.REACT_APP_BLINKPAY_AMOUNT);

export interface SnipcartItem {
    id: string;
    name: string;
    price: number;
    quantity: number;
    url: string;
    image?: string;
    shippable: boolean;
}

// The parts of the Snipcart JavaScript SDK this app uses; see https://docs.snipcart.com/v3/sdk/api.
interface SnipcartSdk {
    api: {
        cart: {
            items: {
                add(item: SnipcartItem): Promise<unknown>;
                update(item: {uniqueId: string; quantity: number}): Promise<unknown>;
            }
        };
        theme: {cart: {open(): void}};
    };
    store: {getState(): {cart: {items: {items: Array<{uniqueId: string; id: string}>}}}};
}

declare global {
    interface Window {
        SnipcartSettings?: {publicApiKey: string; currency: string; version: string};
        Snipcart?: SnipcartSdk;
    }
}

export const loadSnipcart = (): void => {
    if (!snipcartConfigured || document.getElementById('snipcart')) {
        return;
    }

    // Snipcart defaults to USD; the currency must also be enabled in the Snipcart dashboard.
    window.SnipcartSettings = {
        publicApiKey: SNIPCART_PUBLIC_API_KEY as string,
        currency: 'nzd',
        version: SNIPCART_VERSION
    };

    const container = document.createElement('div');
    container.id = 'snipcart';
    container.hidden = true;
    document.body.appendChild(container);

    const stylesheet = document.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = `${SNIPCART_CDN}/themes/v${SNIPCART_VERSION}/default/snipcart.css`;
    document.head.prepend(stylesheet);

    const script = document.createElement('script');
    script.src = `${SNIPCART_CDN}/themes/v${SNIPCART_VERSION}/default/snipcart.js`;
    script.async = true;
    document.head.appendChild(script);
};

// Puts the item in Snipcart's cart at the chosen quantity and opens the cart. Going through
// the SDK rather than a declarative snipcart-add-item button lets the click pass through the
// same production confirmation popup as the other payment buttons. Snipcart keeps its cart
// in the browser between visits, so a line that is already there is updated rather than
// added to.
export const addToSnipcart = async (item: SnipcartItem): Promise<void> => {
    const snipcart = window.Snipcart;
    if (!snipcart) {
        throw new Error('Snipcart has not finished loading yet. Please try again in a moment.');
    }
    const existing = snipcart.store.getState().cart.items.items.find(line => line.id === item.id);
    if (existing) {
        await snipcart.api.cart.items.update({uniqueId: existing.uniqueId, quantity: item.quantity});
    } else {
        await snipcart.api.cart.items.add(item);
    }
    snipcart.api.theme.cart.open();
};
